/**
 * Class to manage dot buttons for an Embla carousel
 */
export class EmblaDotButtons {
  /**
   * @param {Object} emblaApi - The Embla carousel API instance
   * @param {HTMLElement} emblaRoot - The root element of the Embla carousel
   */
  constructor(emblaApi, emblaRoot) {
    this.emblaApi = emblaApi;
    this.emblaRoot = emblaRoot;
    this.dotsNode = null;
    this.dotNodes = [];
    this.containerId = `embla-container-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    this.init();
  }

  /**
   * Initialize the dot buttons
   * @private
   */
  init() {
    this.setContainerId();
    this.createDots();
    this.emblaApi
      .on("init", this.handleCarouselChange.bind(this))
      .on("reInit", this.handleCarouselChange.bind(this))
      .on("select", this.toggleDotBtnsActive.bind(this))
      .on("resize", this.handleCarouselChange.bind(this));
  }

  /**
   * Set a unique ID for the container
   * @private
   */
  setContainerId() {
    const container = this.emblaRoot.querySelector(".embla__container");
    if (container) {
      container.id = this.containerId;
      container.setAttribute("aria-live", "polite");
      container.setAttribute("aria-atomic", "true");
    }
  }

  /**
   * Create the dots structure
   * @private
   */
  createDots() {
    if (this.dotsNode) {
      this.dotsNode.remove();
    }

    this.dotsNode = document.createElement("div");
    this.dotsNode.className = "embla__dots";
    this.dotsNode.setAttribute("role", "tablist");

    const dotButtons = this.emblaApi.scrollSnapList().map((_, index) => {
      const dotButton = document.createElement("button");
      dotButton.className = "embla__dot";
      dotButton.type = "button";
      dotButton.setAttribute("role", "tab");
      dotButton.setAttribute("aria-label", `Gehe zu Slide ${index + 1}`);
      dotButton.setAttribute("aria-controls", this.containerId);
      dotButton.setAttribute("aria-selected", index === 0 ? "true" : "false");

      dotButton.addEventListener(
        "click",
        () => this.emblaApi.scrollTo(index),
        false
      );

      return dotButton;
    });

    dotButtons.forEach((dot) => this.dotsNode.appendChild(dot));
    this.dotNodes = dotButtons;

    this.emblaRoot.appendChild(this.dotsNode);
    this.updateDotsVisibility();
  }

  /**
   * Handle carousel changes (init, reInit, resize)
   * @private
   */
  handleCarouselChange() {
    const currentSlideCount = this.dotNodes.length;
    const newSlideCount = this.emblaApi.scrollSnapList().length;

    if (currentSlideCount !== newSlideCount) {
      this.createDots(); // Recreate dots if the number of slides has changed
    } else {
      this.updateDotsVisibility();
      this.toggleDotBtnsActive();
    }
  }

  /**
   * Toggle the active state of dot buttons
   * @private
   */
  toggleDotBtnsActive() {
    const previous = this.emblaApi.previousScrollSnap();
    const selected = this.emblaApi.selectedScrollSnap();
    this.dotNodes[previous].setAttribute("aria-selected", "false");
    this.dotNodes[selected].setAttribute("aria-selected", "true");
  }

  /**
   * Update the visibility of dots based on the number of slides
   * @private
   */
  updateDotsVisibility() {
    const slideCount = this.emblaApi.scrollSnapList().length;

    if(slideCount > 1) {
      this.emblaRoot.dataset.scrollable = "true";
    }
    else {
      this.emblaRoot.dataset.scrollable = "false";
      this.dotsNode.style.display = "none";
    }

  }
}
